if (!customElements.get('cancel-order-modal')) {
  class CancelOrderModal extends ModalDialog {
    constructor() {
      super();
      this.orderID = null;
      this.orderToken = ``;
      this.isCancellable = false;
      this.orderTitle = ``;
      this.orderPrice = ``;
      this.apiBaseUrl = window.gopuffShopify ? window.gopuffShopify.settings.api_base_url : window.checkout.api_base_url;
      this.submit = this.querySelector(`button[type="submit"]`);
      this.validationFields = this.querySelectorAll(`[required]`);
      this.onChange();
      this.addEventListener(`change`, this.onChange);
      this.addEventListener(`submit`, this.onSubmit);
    }

    hide() {
      document.body.classList.remove('overflow-hidden');
      document.body.dispatchEvent(new CustomEvent('modalClosed'));
      this.removeAttribute('open');
      removeTrapFocus(this.openedBy);
      window.pauseAllMedia();
      this.resetModal();
    }

    resetModal() {
      this.validationFields.forEach((field) => {
        field.value = ``;
      });

      this.setOrder();
    }

    setOrder({ orderID = null, orderToken = ``, isCancellable = false, orderName = ``, orderPrice = `` } = {}) {
      this.orderID = orderID;
      this.orderToken = orderToken;
      this.isCancellable = isCancellable;
      this.orderPrice = orderPrice;
      this.orderName = orderName;
      this.renderHeading();
      this.renderDetails();
      this.onChange();
    }

    renderHeading(orderName = this.orderName) {
      const headingEl = this.querySelector(`.cancel-order-modal__heading`);
      const heading = orderName
        ? `Cancel Order ${orderName}`
        : `Cancel Order`;

      headingEl.innerHTML = heading;
    }

    renderDetails() {
      const detailsEl = this.querySelector(`.cancel-order-modal__content-info`);
      const details = `Are you sure you want to cancel your order? You will receive a refund in the amount of {{ price }} back to your
      original payment method within 10 business days. If you need help with something else,
      <a href="/pages/help-center" target="_blank">contact support</a>.`.split(`{{ price }}`).join(this.orderPrice);

      detailsEl.innerHTML = details;
    }

    onChange() {
      const isValid = this.validateForm();

      this.submit.toggleAttribute(`disabled`, !isValid);
    }

    onSubmit(e) {
      e.preventDefault();
      const isValid = this.validateForm();

      if (!isValid) { return; }

      this.cancelOrder();
      this.hide();
    }

    validateForm() {
      let isValid = Boolean(this.orderID && this.isCancellable);

      this.validationFields.forEach((field) => {
        if (field.value) { return; }

        isValid = false;
      });

      return isValid;
    }

    getIsCancellable(orderID = this.orderID, orderToken = this.orderToken, attempts = 1, retryDelay = 10000) {
      return new Promise(async (resolve) => {
        const isCancellable = await this.getIsCancellableHelper(orderID, orderToken) || false;

        if (!isCancellable && (attempts > 1)) {
          setTimeout(async () => {
            return resolve(await this.getIsCancellable(orderID, orderToken, attempts - 1));
          }, retryDelay);
        } else {
          return resolve(isCancellable);
        }
      });
    }

    async getIsCancellableHelper(orderID, orderToken) {
      try {
        const response = await fetch(`${this.apiBaseUrl}/account/orders/${orderID}/cancellable?shop=${window.Shopify.shop}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ "token": orderToken }),
        });
        const responseJSON = await response.json();

        return responseJSON.cancellable;
      } catch (err) {
        console.log(`error getting /cancellable`);
      }
    }

    async cancelOrder(orderID = this.orderID, orderToken = this.orderToken) {
      const response = await fetch(`${this.apiBaseUrl}/account/orders/${orderID}/cancel?shop=${window.Shopify.shop}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ "token": orderToken }),
      });

      const responseJSON = await response.json();
      const isSuccess = responseJSON.code === 200;
      const link = document.querySelector(`cancel-order-link[order-id="${orderID}"]`);

      if (isSuccess && link) {
        link.setIsCancellable(false);
      }

      const className = isSuccess ? `success` : `error`;
      const message = isSuccess 
        ? `Your order was successfully canceled and you will receive a refund of {{ price }} within 10 business days.`.split(`{{ price }}`).join(this.orderPrice) 
        : `Sorry, we were unable to cancel your order because its status has changed. Please <a href="/pages/help-center" target="_blank">contact support</a> with any further questions.`;

      toast({ message, className, onReload: isSuccess });
    }
  }

  customElements.define(`cancel-order-modal`, CancelOrderModal);
}

if (!customElements.get('cancel-order-link')) {
  class CancelOrderLink extends HTMLElement {
    constructor() {
      super();

      this.cancelOrderModal = document.getElementById('CancelOrderModal');
      this.orderID = this.getAttribute(`order-id`);
      this.orderToken = this.getAttribute(`order-token`);
      this.orderName = this.getAttribute(`order-name`);
      this.orderPrice = this.getAttribute(`order-price`);
      this.attempts = Number(this.getAttribute(`attempts`)) || 1;
      this.isCancellable = false;

      if (!this.orderID || !this.orderToken) { return; }

      this.renderContent();
      this.setIsCancellable();
      this.addEventListener('click', this.openModal.bind(this));
      this.addEventListener('keydown', (e) => {
        if (e.key !== ' ' && e.key !== 'Enter') { return; }

        this.openModal(e);
      });
    }

    renderContent() {
      const link = document.createElement(`a`);
      link.innerHTML = `Cancel Order`;
      link.href = `#`;
      link.addEventListener(`click`, (e) => {
        e.preventDefault();
      });

      this.appendChild(link);
    }

    async setIsCancellable(isCancellable) {
      this.isCancellable = (typeof isCancellable === `boolean`)
        ? isCancellable
        : await this.cancelOrderModal.getIsCancellable(this.orderID, this.orderToken, this.attempts);

      this.setStyles(this.isCancellable);
    }

    openModal(evt) {
      this.cancelOrderModal.setOrder({
        orderID: this.orderID,
        orderToken: this.orderToken,
        orderName: this.orderName,
        orderPrice: this.orderPrice,
        isCancellable: this.isCancellable,
      });

      this.cancelOrderModal.show(evt.currentTarget);
    }

    setStyles(showLink = false) {
      this.classList.toggle(`cancel-order-link--enabled`, showLink);

      const sibling = this.previousElementSibling;

      if (!sibling) { return; }

      sibling.classList.toggle(`cancel-enabled`, showLink);
    }
  }

  customElements.define(`cancel-order-link`, CancelOrderLink);
}